import { jsPDF } from "jspdf";
import {
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion, addPageWithHeader,
  drawPatientIdentityRows, renderCategoryPage, classifyInvestigation,
  RULE_COLOR, RULE_WIDTH,
  type PrescriptionMedicine, type InvestigationCategory,
} from "./visitSummaryLayout";
import { fmtDatePdf } from "@emr/shared/india";
import { groupTaperSteps } from "@emr/shared";

// 2026-11-01, Batch 7 Wave 2 dedupe — the helpers that used to be inlined in
// this renderer (drawMixedLine, drawPatientIdentityRows, classifyInvestigation,
// renderDetailsTable, renderCategoryPage), the RULE_COLOR/RULE_WIDTH ruling
// constants, and the PrescriptionMedicine / InvestigationCategory types were
// extracted verbatim to ./layoutShared.ts and are re-exported through this
// renderer's layout sibling (visitSummaryLayout). computeAgeYm / formatAgeLabel
// moved with drawPatientIdentityRows (their only uses here). The former public
// exports are preserved unchanged for importers:
export {
  type PrescriptionMedicine,
  type InvestigationCategory,
  classifyInvestigation,
} from "./visitSummaryLayout";

export interface VisitSummaryData {
  patientName: string;
  clinicUhid: string;
  patientDob: string | null;
  patientGender: string | null;
  checkInAt: string;
  doctorName: string | null;
  doctorQualifications: string | null;
  doctorRegNumber: string | null;
  note: {
    subjective: string | null;
    objective: string | null;
    diagnosis: string | null;
    plan: string | null;
  } | null;
  prescription: {
    medicines: PrescriptionMedicine[];
    // When true, composition prints in parentheses next to the drug name;
    // when false, name only (strength is never appended — the name carries
    // it when required).
    showComposition: boolean;
    reviewNeeded: boolean;
    reviewDate: string | null;
  } | null;
  investigationRequest: {
    diagnosis: string | null;
    notes: string | null;
    radiologyNotes: string | null;
    electrophysiologyNotes: string | null;
    selectedInvestigationIds: string[];
    investigationDetails?: Record<string, { regionAndSide: string; clinicalNotes: string }>;
  } | null;
  investigations: { id: string; name: string; category: string | null }[];
}

export async function createVisitSummaryPdf(data: VisitSummaryData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  // Page 1: SOAP.
  // One blank row between the header and the start of the visit summary,
  // then the centered underlined "Visit Summary" heading (16pt bold).
  y += mmToPt(5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(124, 45, 18);
  doc.text("Visit Summary", x + w / 2, y, { align: "center" });
  // Underline the heading (0.75pt rule 1.5pt below the text baseline).
  const headingWidth = doc.getTextWidth("Visit Summary");
  doc.setDrawColor(124, 45, 18);
  doc.setLineWidth(0.75);
  doc.line(x + w / 2 - headingWidth / 2, y + 1.5, x + w / 2 + headingWidth / 2, y + 1.5);
  y += mmToPt(8);

  // Patient identity rows — same layout as the prescription info block
  // (Name/Date, Age+Sex/UHID; bold descriptors, normal values).
  y = drawPatientIdentityRows(doc, {
    patientName: data.patientName,
    clinicUhid: data.clinicUhid,
    patientDob: data.patientDob,
    patientGender: data.patientGender,
    checkInAt: data.checkInAt,
  }, y);

  // One blank row between the identity rows and the clinical sections.
  y += mmToPt(5);

  const n = data.note;
  const sections: [string, string | null][] = [
    ["Presenting Complaints", n?.subjective ?? null],
    ["Findings", n?.objective ?? null],
    ["Diagnosis", n?.diagnosis ?? null],
    ["Plan", n?.plan ?? null],
  ];

  for (const [label, content] of sections) {
    y = await ensureSpace(doc, y, mmToPt(14));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(124, 45, 18);
    doc.text(label, x, y);
    y += mmToPt(5);

    y += mmToPt(2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(26, 20, 19);
    if (content) {
      const lines = doc.splitTextToSize(content, w);
      for (const line of lines) {
        y = await ensureSpace(doc, y, mmToPt(5));
        doc.text(line, x, y);
        y += mmToPt(5);
      }
    } else {
      doc.setTextColor(150, 140, 130);
      doc.text("\u2014", x, y);
      y += mmToPt(5);
      doc.setTextColor(26, 20, 19);
    }
    y += mmToPt(3);
  }

  // ---- Prescription table (inline on page 1, one row after the Plan) ----
  // Reuses the same column layout / taper rendering as the standalone
  // prescription PDF (page 3 of the old layout — now removed). One blank
  // row (8.5mm pitch) separates Plan from the table header.
  const RX_LINE_H = mmToPt(8.5); // double-spacing pitch for 12pt table rows
  // Composition prints below the drug name at 8pt (smaller than the 12pt
  // name) on a tighter pitch so it reads as a subtitle. The composition's
  // first baseline sits one full RX_LINE_H below the last name baseline
  // (no overlap, clean row rhythm); subsequent wrap lines are COMP_LINE_H
  // apart. A bottom pad equal to `base` mirrors the top padding.
  const COMP_FONT = 8;
  const COMP_LINE_H = mmToPt(4.5);
  // Gap between the last drug-name baseline and the first composition line.
  // 4mm — the composition sits 4mm below the drug name as a subtitle.
  const COMP_GAP = mmToPt(4);
  const COMP_BOTTOM_PAD = mmToPt(2);
  if (data.prescription && data.prescription.medicines.length > 0) {
    y += RX_LINE_H; // one full double-spaced row of space after the Plan section
    y = await ensureSpace(doc, y, mmToPt(20));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(124, 45, 18);
    doc.text("Prescription", x, y);
    y += mmToPt(7);

    // Table header: Sr. No., Drug, Instructions, Frequency, Days (same layout
    // as the standalone prescription PDF, incl. taper rendering): 12pt text,
    // double-spaced rows (8.5mm pitch), shared cell pad so plain and taper
    // rows align.
    const colSr = mmToPt(17);
    const colFreq = mmToPt(28);
    const colDuration = mmToPt(22);
    const colInstr = mmToPt(42);
    const colDrug = w - colSr - colFreq - colDuration - colInstr;
    const cellPad = mmToPt(2);
    const base = mmToPt(6); // text baseline inside an 8.5mm-pitch line
    // Taper step lines use a tighter baseline (4mm) and pitch (7mm) so the
    // date/freq/days rows sit closer to the drug name header above them.
    const stepBase = mmToPt(4);
    const stepLineH = mmToPt(7);
    const cxSr = x + cellPad;
    const cxDrug = x + cellPad + colSr;
    const cxInstr = cxDrug + colDrug;
    const cxFreq = cxInstr + colInstr;
    const cxDays = cxFreq + colFreq;

    // Wrap a frequency string to the column width. Frequency values are
    // hyphen-separated tokens (e.g. "1-1-1-1") that browsers/jsPDF treat as
    // unbreakable. We insert spaces around hyphens so splitTextToSize can
    // wrap at those break points, preventing overflow into adjacent columns.
    const wrapFreq = (freq: string | null | undefined): string[] => {
      if (!freq) return ["" as string];
      const spaced = freq.replace(/(\s*-\s*)/g, " - ").trim();
      return doc.splitTextToSize(spaced, colFreq - cellPad - mmToPt(1)) as string[];
    };

    // Header row — extracted into a function so it can be repeated at the
    // top of each continuation page when the table flows across pages
    // (mirrors the standalone prescription PDF's table).
    const drawTableHeader = (headerY: number): number => {
      doc.setFillColor(247, 240, 232);
      doc.rect(x, headerY, w, mmToPt(8), "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(124, 45, 18);
      doc.text("Sr. No.", cxSr, headerY + mmToPt(5.5));
      doc.text("Drug", cxDrug, headerY + mmToPt(5.5));
      doc.text("Instructions", cxInstr, headerY + mmToPt(5.5));
      doc.text("Frequency", cxFreq, headerY + mmToPt(5.5));
      doc.text("Days", cxDays, headerY + mmToPt(5.5));
      doc.setFont("helvetica", "normal");
      doc.setTextColor(26, 20, 19);
      doc.setFontSize(12);
      return headerY + mmToPt(8);
    };

    const tableTop = y;
    y = drawTableHeader(y);
    // Top of the table on the current page (the ruling pass frames each
    // page segment from here); updated on page breaks in ensureSpaceRuled.
    const pageTopRef = { val: tableTop };
    // Row boundary Ys (header bottom + bottom of each medicine row) for the
    // horizontal row separators; reset on page breaks.
    let rowBoundaries: number[] = [y];

    const showComp = data.prescription.showComposition;
    // Column boundaries for the vertical separators (computed once, used on
    // every page the table spans) — text starts one cellPad right of each
    // boundary, so the separators sit between No.|Drug, Drug|Instructions,
    // Instructions|Frequency, Frequency|Days.
    const colBoundaries = [cxDrug - cellPad, cxInstr, cxFreq, cxDays];

    // Ruling pass for the current page segment (2026-10-26, mirrors the
    // prescription PDF): ALL table ruling at the single visible weight —
    // vertical column separators, the outer frame, and horizontal row
    // dividers (0.5pt warm gray 150,138,128) so they print cleanly.
    const drawSeparators = (pageTop: number, endY: number) => {
      doc.setDrawColor(...RULE_COLOR);
      doc.setLineWidth(RULE_WIDTH);
      for (const bx of colBoundaries) {
        doc.line(bx, pageTop, bx, endY);
      }
      doc.rect(x, pageTop, w, endY - pageTop);
      for (const hy of rowBoundaries) {
        if (hy > pageTop + 0.5 && hy < endY - 0.5) {
          doc.line(x, hy, x + w, hy);
        }
      }
    };

    // ensureSpace wrapper that keeps the ruling in sync across page breaks:
    // when the table flows to a new page, draw separators for the page that
    // just ended, add the new page, repeat the table header, and reset the
    // row boundaries. Rows are never split mid-block (callers pass the full
    // block height as neededHeight). The new page's frame starts at the
    // header band TOP (pageStart) — matching page 1, where tableTop is
    // captured before drawTableHeader — so the header band is framed and
    // the vertical separators cross it on every page the table spans.
    const ensureSpaceRuled = async (curY: number, neededHeight: number): Promise<number> => {
      const region = getTextRegion(doc);
      const bottomLimit = region.y + region.maxHeight;
      if (curY + neededHeight <= bottomLimit) return curY;
      drawSeparators(pageTopRef.val, curY);
      const pageStart = await addPageWithHeader(doc);
      const headerBottom = drawTableHeader(pageStart);
      rowBoundaries = [headerBottom];
      pageTopRef.val = pageStart;
      return headerBottom;
    };

    for (let mi = 0; mi < data.prescription.medicines.length; mi++) {
      const m = data.prescription.medicines[mi];
      // Drug name only on the main line; composition prints on its own line
      // below at 8pt (no parentheses) when showComposition is on.
      const drugLines = doc.splitTextToSize(m.name, colDrug - cellPad - mmToPt(1)) as string[];
      // Measure composition at COMP_FONT (8pt) — splitTextToSize uses the
      // current font size, so we must set it to 8pt before wrapping. If the
      // font is left at 12pt, longer compositions wrap to 2 lines at 12pt
      // even though they fit on 1 line at 8pt — the block height would then
      // reserve space for 2 lines while drawComp draws only 1, leaving a gap.
      doc.setFontSize(COMP_FONT);
      const compLines = (showComp && m.composition && m.composition.trim())
        ? doc.splitTextToSize(m.composition.trim(), colDrug - cellPad - mmToPt(1)) as string[]
        : [];
      doc.setFontSize(12);

      const instrLines = (!m.taper || m.taper.length === 0) && m.instructions
        ? doc.splitTextToSize(m.instructions, colInstr - cellPad - mmToPt(1)) as string[]
        : ["" as string];
      const freqLines = (!m.taper || m.taper.length === 0) ? wrapFreq(m.frequency) : ["" as string];
      // Name block height: name's first baseline is `base` from the row top.
      // The composition's first baseline sits COMP_GAP (4mm) below the last
      // name baseline — a subtitle gap. Subsequent composition wrap lines are
      // COMP_LINE_H apart. A bottom pad of COMP_BOTTOM_PAD (2mm) gives a
      // small gap below the composition. The row must also be at least one
      // RX_LINE_H tall and at least as tall as the wrapped instructions or
      // wrapped frequency (whichever is taller).
      const nameBlockH = compLines.length > 0
        ? base + RX_LINE_H * (drugLines.length - 1) + COMP_GAP + COMP_LINE_H * (compLines.length - 1) + COMP_BOTTOM_PAD
        : RX_LINE_H;
      const mainH = Math.max(nameBlockH, RX_LINE_H * Math.max(instrLines.length, freqLines.length, 1));

      // Group taper steps by drug name (per-strength protocol). Single group
      // → legacy single-header layout; multiple groups → one header per
      // consecutive same-name run, each followed by its date/freq/days lines.
      const groups = m.taper && m.taper.length > 0 ? groupTaperSteps(m.taper, m.name) : null;
      const multiGroup = !!(groups && groups.length > 1);

      let blockH = mainH;
      if (groups) {
        if (multiGroup) {
          // Multi-group: each group draws a header (drug name + optional
          // composition line at 8pt) + its step lines. The first group's
          // header reuses mainH (carries the sr.no); subsequent groups add
          // their own header height (name lines + composition lines).
          blockH = 0;
          for (let gi = 0; gi < groups.length; gi++) {
            const g = groups[gi];
            const gLines = doc.splitTextToSize(g.name, colDrug - cellPad - mmToPt(1)) as string[];
            const gComp = (g.steps.find((s) => s.composition && s.composition.trim())?.composition ?? "").trim() || null;
            const gCompCount = showComp && gComp
              ? (doc.setFontSize(COMP_FONT), (doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length)
              : 0;
            if (doc.getFontSize() !== 12) doc.setFontSize(12);
            const headerH = gCompCount > 0
              ? base + RX_LINE_H * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + COMP_BOTTOM_PAD
              : Math.max(RX_LINE_H * gLines.length, RX_LINE_H);
            blockH += (gi === 0 ? Math.max(headerH, mainH) : headerH);
            for (const step of g.steps) {
              const sInstr = step.instructions
                ? doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[]
                : ["" as string];
              const sFreqCount = wrapFreq(step.frequency).length;
              blockH += stepLineH * Math.max(sInstr.length, sFreqCount, 1);
            }
          }
        } else {
          for (const step of groups[0].steps) {
            const sInstr = step.instructions
              ? doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[]
              : ["" as string];
            const sFreqCount = wrapFreq(step.frequency).length;
            blockH += stepLineH * Math.max(sInstr.length, sFreqCount, 1);
          }
        }
      }

      y = await ensureSpaceRuled(y, blockH);
      if (mi % 2 === 1) {
        doc.setFillColor(252, 248, 243);
        doc.rect(x, y, w, blockH, "F");
      }

      doc.setTextColor(26, 20, 19);
      doc.text(String(m.serialNo), cxSr, y + base);

      // Draw composition lines (8pt, no parens) below a drug name. The
      // composition's first baseline sits COMP_GAP below the LAST drug-name
      // line baseline (passed as lastNameBaselineY), and subsequent lines are
      // spaced COMP_LINE_H apart. Returns the number of lines drawn.
      const drawComp = (comp: string | null, lastNameBaselineY: number): number => {
        if (!showComp || !comp || !comp.trim()) return 0;
        doc.setFontSize(COMP_FONT);
        const lines = doc.splitTextToSize(comp.trim(), colDrug - cellPad - mmToPt(1)) as string[];
        const startY = lastNameBaselineY + COMP_GAP;
        for (let i = 0; i < lines.length; i++) {
          doc.text(lines[i], cxDrug, startY + COMP_LINE_H * i);
        }
        doc.setFontSize(12);
        return lines.length;
      };

      // Plain medicine (no taper): drug name on the main line + instr/freq/days.
      if (!m.taper || m.taper.length === 0) {
        doc.setFont("helvetica", "bold");
        doc.text(drugLines[0], cxDrug, y + base);
        doc.setFont("helvetica", "normal");
        if (drugLines.length > 1) {
          for (let i = 1; i < drugLines.length; i++) {
            doc.text(drugLines[i], cxDrug, y + base + RX_LINE_H * i);
          }
        }
        // Composition on its own line below the drug name, 8pt.
        if (compLines.length > 0) {
          drawComp(m.composition, y + base + RX_LINE_H * (drugLines.length - 1));
        }
        if (m.instructions) {
          for (let i = 0; i < instrLines.length; i++) {
            doc.text(instrLines[i], cxInstr, y + base + RX_LINE_H * i);
          }
        }
        for (let i = 0; i < freqLines.length; i++) {
          doc.text(freqLines[i], cxFreq, y + base + RX_LINE_H * i);
        }
        doc.text(m.duration ?? "", cxDays, y + base);
        y += mainH;
        rowBoundaries.push(y);
        continue;
      }

      if (!groups) { y += mainH; rowBoundaries.push(y); continue; }

      const tabIndent = mmToPt(6);

      if (multiGroup) {
        // Per-strength protocol: one drug-name header per group, with the
        // date/freq/days lines indented beneath each group.
        for (let gi = 0; gi < groups.length; gi++) {
          const g = groups[gi];
          const gLines = doc.splitTextToSize(g.name, colDrug - cellPad - mmToPt(1)) as string[];
          const gComp = (g.steps.find((s) => s.composition && s.composition.trim())?.composition ?? "").trim() || null;
          const gCompCount = showComp && gComp
            ? (doc.setFontSize(COMP_FONT), (doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length)
            : 0;
          if (doc.getFontSize() !== 12) doc.setFontSize(12);
          const gHeaderH = gCompCount > 0
            ? base + RX_LINE_H * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + COMP_BOTTOM_PAD
            : Math.max(RX_LINE_H * gLines.length, RX_LINE_H);
          if (gi === 0) {
            // First group header shares the main line (sr.no already drawn).
            doc.setFont("helvetica", "bold");
            doc.text(gLines[0], cxDrug, y + base);
            doc.setFont("helvetica", "normal");
            if (gLines.length > 1) {
              for (let i = 1; i < gLines.length; i++) {
                doc.text(gLines[i], cxDrug, y + base + RX_LINE_H * i);
              }
            }
            if (gCompCount > 0) {
              drawComp(gComp, y + base + RX_LINE_H * (gLines.length - 1));
            }
            y += Math.max(gHeaderH, mainH);
          } else {
            const gCompCount = showComp && gComp
              ? (doc.setFontSize(COMP_FONT), (doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length)
              : 0;
            if (doc.getFontSize() !== 12) doc.setFontSize(12);
            const headerH = gCompCount > 0
              ? base + RX_LINE_H * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + COMP_BOTTOM_PAD
              : Math.max(RX_LINE_H * gLines.length, RX_LINE_H);
            y = await ensureSpaceRuled(y, headerH);
            doc.setFont("helvetica", "bold");
            doc.text(gLines[0], cxDrug, y + base);
            doc.setFont("helvetica", "normal");
            if (gLines.length > 1) {
              for (let i = 1; i < gLines.length; i++) {
                doc.text(gLines[i], cxDrug, y + base + RX_LINE_H * i);
              }
            }
            if (gCompCount > 0) {
              drawComp(gComp, y + base + RX_LINE_H * (gLines.length - 1));
            }
            y += headerH;
          }
          for (const step of g.steps) {
            const [fy, fmo, fd] = step.fromDate.split("-").map(Number);
            const toIso = new Date(Date.UTC(fy, fmo - 1, fd + step.days - 1)).toISOString().slice(0, 10);
            const rangeText = step.days === 1
              ? fmtDatePdf(step.fromDate)
              : `${fmtDatePdf(step.fromDate)} – ${fmtDatePdf(toIso)}`;
            const sInstr = step.instructions
              ? doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[]
              : ["" as string];
            const sFreqLines = wrapFreq(step.frequency);
            const stepH = stepLineH * Math.max(sInstr.length, sFreqLines.length, 1);
            y = await ensureSpaceRuled(y, stepH);
            doc.setFontSize(12);
            doc.setTextColor(60, 50, 45);
            doc.text(rangeText, cxDrug + tabIndent, y + stepBase);
            if (step.instructions) {
              for (let i = 0; i < sInstr.length; i++) {
                doc.text(sInstr[i], cxInstr, y + stepBase + stepLineH * i);
              }
            }
            doc.setTextColor(26, 20, 19);
            for (let i = 0; i < sFreqLines.length; i++) {
              doc.text(sFreqLines[i], cxFreq, y + stepBase + stepLineH * i);
            }
            doc.text(`${step.days} days`, cxDays, y + stepBase);
            y += stepH;
          }
        }
        rowBoundaries.push(y);
        continue;
      }

      // Single-group taper (legacy): drug name on the main line, all steps
      // indented one tab below it.
      doc.setFont("helvetica", "bold");
      doc.text(drugLines[0], cxDrug, y + base);
      doc.setFont("helvetica", "normal");
      if (drugLines.length > 1) {
        for (let i = 1; i < drugLines.length; i++) {
          doc.text(drugLines[i], cxDrug, y + base + RX_LINE_H * i);
        }
      }
      // Composition on its own line below the drug name, 8pt, no parens.
      if (compLines.length > 0) {
        drawComp(m.composition, y + base + RX_LINE_H * (drugLines.length - 1));
      }
      y += mainH;
      for (const step of groups[0].steps) {
        const [fy, fmo, fd] = step.fromDate.split("-").map(Number);
        const toIso = new Date(Date.UTC(fy, fmo - 1, fd + step.days - 1)).toISOString().slice(0, 10);
        const rangeText = step.days === 1
          ? fmtDatePdf(step.fromDate)
          : `${fmtDatePdf(step.fromDate)} – ${fmtDatePdf(toIso)}`;
        const sInstr = step.instructions
          ? doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[]
          : ["" as string];
        const sFreqLines = wrapFreq(step.frequency);
        const stepH = stepLineH * Math.max(sInstr.length, sFreqLines.length, 1);
        y = await ensureSpaceRuled(y, stepH);
        doc.setFontSize(12);
        doc.setTextColor(60, 50, 45);
        doc.text(rangeText, cxDrug + tabIndent, y + stepBase);
        if (step.instructions) {
          for (let i = 0; i < sInstr.length; i++) {
            doc.text(sInstr[i], cxInstr, y + stepBase + stepLineH * i);
          }
        }
        doc.setTextColor(26, 20, 19);
        for (let i = 0; i < sFreqLines.length; i++) {
          doc.text(sFreqLines[i], cxFreq, y + stepBase + stepLineH * i);
        }
        doc.text(`${step.days} days`, cxDays, y + stepBase);
        y += stepH;
      }
      rowBoundaries.push(y);
    }

    // Final ruling pass for the last page segment (earlier pages were ruled
    // by ensureSpaceRuled before each page break).
    drawSeparators(pageTopRef.val, y);

    // One full blank row (double-spaced pitch) after the table, then review.
    y += RX_LINE_H;
    doc.setFontSize(12);
    if (data.prescription.reviewNeeded) {
      // PRN: review only if the patient needs it.
      doc.text("Review in case of any new symptoms or if required", x, y);
    } else if (data.prescription.reviewDate) {
      doc.text(`Review on: ${fmtDatePdf(data.prescription.reviewDate)}`, x, y);
    } else {
      doc.text("No review needed", x, y);
    }
    // No trailing y increment — nothing after this renders at y; page 2+
    // investigation sheets compute their own start positions.
  }

  // Page 2+: Investigation sheets (shared renderer with the standalone
  // investigation PDF — identity rows + diagnosis, solid round dots).
  if (data.investigationRequest && data.investigations.length > 0) {
    const cats: InvestigationCategory[] = ["Lab", "Radiology", "Electrophysiology"];
    for (const cat of cats) {
      const tests = filterTests(data, cat);
      if (tests.length === 0) continue;
      const notes = cat === "Lab" ? data.investigationRequest.notes
        : cat === "Radiology" ? data.investigationRequest.radiologyNotes
        : data.investigationRequest.electrophysiologyNotes;
      // addPageWithHeader draws the letterhead header+footer so these
      // investigation pages match the standalone investigation sheet
      // (previously they had no letterhead, and the signature's
      // footerTop anchor relied on stale module-level footerHeightPt).
      await addPageWithHeader(doc);
      await renderCategoryPage(doc, {
        patientName: data.patientName,
        clinicUhid: data.clinicUhid,
        patientDob: data.patientDob,
        patientGender: data.patientGender,
        checkInAt: data.checkInAt,
        diagnosis: data.investigationRequest.diagnosis ?? data.note?.diagnosis ?? null,
        category: cat,
        tests,
        notes,
        doctorName: data.doctorName,
        doctorRegNumber: data.doctorRegNumber,
        investigationDetails: data.investigationRequest.investigationDetails,
      });
    }
  }

  return doc;
}

function filterTests(data: VisitSummaryData, cat: InvestigationCategory): { id: string; name: string }[] {
  if (!data.investigationRequest) return [];
  const selected = data.investigationRequest.selectedInvestigationIds;
  const matches = data.investigations.filter((i) => selected.includes(i.id));
  // Lab is the catch-all bucket: any category other than Radiology /
  // Electrophysiology prints on the Lab sheet (classifier in investigationPdf.ts).
  return matches.filter((i) => classifyInvestigation(i.category) === cat);
}
